import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from './UserState';
import { useSubscription } from '@meraki-internal/state';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { ProfileCollectionForm, IProfileCollectionData, IProfileLocationMode, resolveLocationMode } from './ProfileCollectionForm';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { TOS_COMBINED_CONSENT_VERSION, SMS_CONSENT_VERSION, SMS_COMBINED_CONSENT_VERSION, TERMS_URL, SMS_CONSENT_SAVE_TEXT, COMBINED_CONSENT_SAVE_TEXT } from './consentText';
import { Text } from '../components/Text';
import { createUseStyles } from 'react-jss';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';

const useStyles = createUseStyles({
    modal: {
        '--width': '100%',
        '--height': '100%',
        '--border-radius': '0'
    },
    ionContent: {
        height: '100%',
        display: 'flex'
    },
    contentWrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        paddingTop: 60
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 500,
        width: '100%',
        padding: '40px 20px'
    },
    buttons: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 400,
        padding: '20px 0',
        gap: 8
    }
});

export const RequireProfileCheck: FC = () => {
    const classes = useStyles();
    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);

    const orgsState = useInstance(OrganizationsState);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const onboarding = useInstance(UserOnboardingState);
    useSubscription(() => onboarding);

    const [isOpen, setIsOpen] = useState(false);
    const wasSubmittedRef = useRef(false);
    const [hasName, setHasName] = useState(false);

    const [formData, setFormData] = useState<IProfileCollectionData>({
        firstName: '',
        lastName: '',
        location: '',
        phone: ''
    });
    const [isValid, setIsValid] = useState(false);
    const [locationMode, setLocationMode] = useState<IProfileLocationMode>('zip');

    useEffect(() => {
        if (isAnonymous) {
            return;
        }
        if (principal.tokenType === 'caremap-viewer') {
            return;
        }

        // wait for onboarding state before deciding
        if (!onboarding.isLoaded()) {
            return;
        }

        // defer to Welcome if it hasn't been completed yet
        if (!onboarding.isStepComplete('recording')) {
            return;
        }

        // skip if user already dismissed or submitted this session
        if (sessionStorage.getItem('ih:profile-check-skipped')) {
            return;
        }
        if (wasSubmittedRef.current) {
            return;
        }

        // delay to let redirects happen (same pattern as old RequireNameModal)
        const timer = setTimeout(() => {
            const namePresent = Boolean(userState.getName());
            const hasLocation = Boolean(userState.state.address);
            const hasPhone = Boolean(userState.state.phone);
            setHasName(namePresent);
            if (!namePresent || !hasLocation || !hasPhone) {
                const rawPhone = userState.state.phone || '';
                const formattedPhone = rawPhone.length === 10
                    ? `${rawPhone.slice(0, 3)}-${rawPhone.slice(3, 6)}-${rawPhone.slice(6)}`
                    : rawPhone;
                setLocationMode(resolveLocationMode(userState.state.address));
                // a dependency changing while the form is open reruns this and replaces what was typed; left as is
                setFormData(prev => ({
                    ...prev,
                    firstName: userState.state.firstName || '',
                    lastName: userState.state.lastName || '',
                    location: userState.state.address || '',
                    phone: formattedPhone
                }));
                setIsOpen(true);
                mixpanel.track('Profile Collection Viewed', () => ({ Location: 'require-profile-check' }));
            }
        }, 250);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonymous, principal, userState, orgsState, mixpanel, onboarding.state?.loaded, onboarding.state?.steps.recording]);

    const hasTosConsent = userState.state.consents?.some(c => c.consentType === 'terms-of-service') ?? false;
    const consentText = hasTosConsent ? SMS_CONSENT_SAVE_TEXT : COMBINED_CONSENT_SAVE_TEXT;
    const disclosureText = consentText;

    const onSubmit = useCallback(async () => {
        wasSubmittedRef.current = true;

        const cleanPhone = formData.phone.replace(/\D/g, '');
        const existingPhone = userState.state.phone;

        if (formData.firstName) {
            userState.setFirstName(formData.firstName);
        }
        if (formData.lastName) {
            userState.setLastName(formData.lastName);
        }
        if (formData.location) {
            userState.setAddress(formData.location);
        }
        if (formData.phone) {
            userState.setPhone(cleanPhone);
        }

        if (!hasTosConsent) {
            userState.addConsent({
                consentType: 'terms-of-service',
                consentedAt: new Date().toISOString(),
                disclosureVersion: TOS_COMBINED_CONSENT_VERSION,
                disclosureText
            });
        }

        const phoneChanged = cleanPhone && cleanPhone !== existingPhone;
        if (phoneChanged) {
            userState.addConsent({
                consentType: 'sms-consent',
                consentedAt: new Date().toISOString(),
                disclosureVersion: hasTosConsent ? SMS_CONSENT_VERSION : SMS_COMBINED_CONSENT_VERSION,
                disclosureText,
                phone: cleanPhone
            });
        }

        await userState.save();

        mixpanel.track('Profile Collection Completed', () => ({
            Location: 'require-profile-check',
            'Fields Entered': [
                formData.firstName ? 'firstName' : '',
                formData.lastName ? 'lastName' : '',
                // still reported as zipCode so the existing reports keep counting the same field
                formData.location ? 'zipCode' : '',
                formData.phone ? 'phone' : ''
            ].filter(Boolean)
        }));

        setIsOpen(false);
    }, [formData, userState, mixpanel, hasTosConsent, disclosureText]);

    const onSkip = useCallback(() => {
        sessionStorage.setItem('ih:profile-check-skipped', 'true');
        mixpanel.track('Profile Collection Skipped', () => ({ Location: 'require-profile-check' }));
        setIsOpen(false);
    }, [mixpanel]);

    if (!isOpen) {
        return null;
    }

    return (
        <IonModal isOpen data-id="require-profile-overlay" backdropDismiss={false} className={classes.modal}>
            <IonContent className={classes.ionContent}>
                <div className={classes.contentWrapper}>
                <div className={classes.content}>
                    <div style={{textAlign: 'center', marginBottom: 20}}>
                        <div data-id="profile-check-title" style={{fontSize: 24, fontWeight: 'bold'}}>{hasName ? 'Welcome back!' : 'Tell us about yourself'}</div>
                        <div style={{padding: '12px 0 0'}}>
                            <Text type="detail">Bea needs a few more details to personalize your experience.</Text>
                        </div>
                    </div>
                    <ProfileCollectionForm
                        data={formData}
                        onChange={setFormData}
                        onValidityChange={setIsValid}
                        locationMode={locationMode}
                        autoFocus
                    />
                    <div className={classes.buttons}>
                        {hasName && <IonButton fill="clear" type="button" data-id="profile-skip-button" onClick={onSkip}>Skip for now</IonButton>}
                        <div style={{textAlign: 'center', padding: '12px 0 0', fontSize: 12, color: 'var(--ion-color-step-700)'}}>
                            {hasTosConsent ? consentText : (<>
                                {'By selecting "Save" you accept our '}
                                <a href={TERMS_URL} target="_blank" rel="noreferrer" style={{color: 'var(--ion-color-step-700)'}}>Terms</a>
                                {' and consent to receive texts from Innerhive, including tips, updates, and offers. Msg frequency varies; data rates may apply. Text STOP to cancel.'}
                            </>)}
                        </div>
                        <IonButton expand="block" style={{'--border-radius': '16px'}} data-id="profile-save-button" disabled={!isValid} onClick={onSubmit}>Save</IonButton>
                    </div>
                </div>
                </div>
            </IonContent>
        </IonModal>
    );
};
